import { useState, useEffect, useCallback, useMemo } from "react";
import { useAppData } from "../context/AppDataContext";
import { useModal } from "../context/ModalContext";
import { useToast } from "../context/ToastContext";
import { calendarApi } from "../api/calendarApi";
import { ApiError } from "../api/httpClient";
import { LoadingState, ErrorState } from "../components/ApiState";
import EmptyState from "../components/EmptyState";
import DateInput from "../components/DateInput";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const TYPES = [["REEL", "Reel"], ["POST", "Post"], ["CAROUSEL", "Carousel"]];
/** What an entry is called in lists: its title, else its caption (one line), so entries never look empty. */
const entryLabel = (p) => (p.title || (p.caption || "").replace(/\s+/g, " ").trim()).slice(0, 80);

const LEGEND = [["green", "Posted on Time"], ["orange", "Posted by Delay"], ["red", "Not Posted Yet"], ["yellow", "Scheduled in Scheduler"], ["blue", "Upcoming"]];
const MAX_PER_CELL = 3;

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmt12 = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  return `${pad(h % 12 || 12)}:${pad(m)} ${h >= 12 ? "PM" : "AM"}`;
};
const fmtDateLong = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("en-GB", { dateStyle: "medium" }) : "");
const fmtStamp = (iso, tz) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString("en-GB", { timeZone: tz, dateStyle: "medium" })}, ${d.toLocaleTimeString("en-US", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: true })}`;
  } catch { return iso; }
};
const errText = (e) => (e instanceof ApiError ? e.message : "Something went wrong. Please try again.");

/** Month grid: 6 weeks max, Sunday first. */
function buildCells(first) {
  const start = new Date(first.getFullYear(), first.getMonth(), 1 - first.getDay(), 12);
  const lastDay = new Date(first.getFullYear(), first.getMonth() + 1, 0, 12);
  const end = new Date(lastDay.getFullYear(), lastDay.getMonth(), lastDay.getDate() + (6 - lastDay.getDay()), 12);
  const cells = [];
  for (let d = new Date(start); d <= end; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 12)) {
    cells.push({ key: ymd(d), day: d.getDate(), outside: d.getMonth() !== first.getMonth() });
  }
  return cells;
}

function statusLabel(status) {
  return status.code === "OVERDUE" ? "Not Posted Yet" : status.label;
}

function StatusBadge({ status }) {
  return <span className={`cal-badge ${status.color}`}>{statusLabel(status)}</span>;
}

/** Add / edit a PLAN. Saving never publishes anything. */
export function PlanForm({ accountId, accountName, plan, defaultDate, onSaved, onCancel }) {
  const [f, setF] = useState(() => ({
    content_type: plan?.content_type || "REEL", planned_date: plan?.planned_date || defaultDate || ymd(new Date()), planned_time: plan?.planned_time || "18:00",
    title: plan?.title || "", caption: plan?.caption || "",
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const save = async () => {
    if (!f.planned_date) { setErr("Enter a valid planned date in DD/MM/YYYY format."); return; }
    setBusy(true); setErr(null);
    try {
      // Everything planned here is published directly on Instagram; Scheduler jobs show up on the Calendar by themselves.
      const body = { ...f, publish_method: "DIRECT", scheduler_content_id: null };
      if (plan) await calendarApi.update(plan.id, body);
      else await calendarApi.create({ ...body, account_id: accountId });
      onSaved(plan ? "Calendar entry updated." : "Added to the calendar.");
    } catch (e) { setErr(errText(e)); setBusy(false); }
  };

  return (
    <div className="cal-form">
      <h3>{plan ? "Edit planned content" : "Plan content"}</h3>
      <p className="cal-form-note">Planning only — saving this does not publish anything to Instagram.</p>
      <div className="cal-form-grid">
        <label><span>Instagram account</span><input value={`@${accountName}`} disabled /></label>
        <label><span>Content type</span>
          <select value={f.content_type} onChange={set("content_type")}>{TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
        <label><span>Planned date</span><DateInput aria-label="Planned date" value={f.planned_date} onChange={set("planned_date")} /></label>
        <label><span>Planned time</span><input type="time" value={f.planned_time} onChange={set("planned_time")} /></label>
        <label className="wide"><span>Title (internal, optional)</span><input maxLength={200} value={f.title} onChange={set("title")} placeholder="e.g. Diwali launch reel" /></label>
        <label className="wide"><span>Expected caption (required)</span><textarea rows={2} maxLength={2200} value={f.caption} onChange={set("caption")} placeholder="Describe the expected caption. Wording can differ; verification uses caption meaning and publishing details." /></label>
      </div>
      {err && <div className="cal-error">{err}</div>}
      <div className="modal-actions">
        <button className="btn btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
}

/** Read-only view of a Scheduler job that the Calendar shows automatically. */
function AutoDetail({ p, accountName, tz, onClose }) {
  const row = (label, value) => value ? <div className="modal-detail-row"><span className="label">{label}</span><span className="value">{value}</span></div> : null;
  return (
    <div className="cal-form">
      <button className="modal-close" onClick={onClose} aria-label="Close"><svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg></button>
      <div className="cal-detail-head"><span className="tag tag-reel">{p.kind_label}</span><StatusBadge status={p.status} /></div>
      <h3>{p.kind_label} for @{accountName}</h3>
      {p.status.delay_text && <div className="cal-delay">{p.status.delay_text}</div>}
      {p.status.note && <p className="cal-note">{p.status.note}</p>}
      {row("Instagram account", `@${accountName}`)}
      {row("Scheduled for", `${fmtDateLong(p.planned_date)}, ${fmt12(p.planned_time)}`)}
      {row("Publishing method", "Instagram Scheduler")}
      {row("Scheduler job", `#${p.scheduler.id} · ${p.scheduler.status}`)}
      {row("Scheduled by", p.created_by_name)}
      {row("Actual publish time", p.actual_published_at ? fmtStamp(p.actual_published_at, tz) : null)}
      {p.permalink && <div className="modal-detail-row"><span className="label">Instagram post</span><span className="value"><a href={p.permalink} target="_blank" rel="noreferrer">View on Instagram</a></span></div>}
      {row("Caption", p.caption)}
      <p className="cal-note">This entry comes straight from the Instagram Scheduler. Edit or cancel it on the Scheduled page.</p>
      <div className="modal-actions"><button className="btn btn-primary btn-block" onClick={onClose}>Close</button></div>
    </div>
  );
}

function PlanDetail({ planId, accountName, tz, onChanged, onEdit, onClose }) {
  const { showToast } = useToast();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setP(await calendarApi.plan(planId)); } catch (e) { setErr(errText(e)); }
  }, [planId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (p?.matched_media_id || p?.actual_published_at) return undefined;
    const timer = setInterval(() => { if (document.visibilityState === "visible") load(); }, 5000);
    return () => clearInterval(timer);
  }, [p?.matched_media_id, p?.actual_published_at, load]);

  const del = async () => {
    const linked = p.scheduler?.id ? " The linked Scheduler job will stay as it is." : "";
    if (!window.confirm(`Delete this calendar entry?${linked} Nothing on Instagram is affected.`)) return;
    setBusy(true);
    try { await calendarApi.remove(p.id); showToast("Calendar entry deleted."); onChanged(); onClose(); } catch (e) { setErr(errText(e)); setBusy(false); }
  };

  if (!p) return <div className="cal-form">{err ? <div className="cal-error">{err}</div> : <p>Loading…</p>}<div className="modal-actions"><button className="btn btn-primary btn-block" onClick={onClose}>Close</button></div></div>;
  const row = (label, value) => value ? <div className="modal-detail-row"><span className="label">{label}</span><span className="value">{value}</span></div> : null;
  return (
    <div className="cal-form">
      <button className="modal-close" onClick={onClose} aria-label="Close"><svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg></button>
      <div className="cal-detail-head"><span className="tag tag-reel">{p.kind_label}</span><StatusBadge status={p.status} /></div>
      <h3>{p.title || `${p.kind_label} for @${accountName}`}</h3>
      {p.status.delay_text && <div className="cal-delay">{p.status.delay_text}</div>}
      {p.status.note && <p className="cal-note">{p.status.note}</p>}
      {row("Instagram account", `@${accountName}`)}
      {row("Planned", `${fmtDateLong(p.planned_date)}, ${fmt12(p.planned_time)}`)}
      {row("Publishing method", p.publish_method === "SCHEDULER" ? "Instagram Scheduler" : "Direct Instagram")}
      {p.scheduler && row("Linked Scheduler job", `#${p.scheduler.id} · ${p.scheduler.status || "not found"}`)}
      {p.scheduler?.scheduled_at && row("Scheduler job runs at", fmtStamp(p.scheduler.scheduled_at, tz))}
      {p.scheduler?.scheduled_at && p.scheduler.scheduled_at.slice(0, 16) !== p.planned_at.slice(0, 16) && <p className="cal-note">The planned time above differs from the Scheduler job's time. Delay is measured against the planned time; use Edit to change it.</p>}
      {row("Actual publish time", p.actual_published_at ? fmtStamp(p.actual_published_at, tz) : null)}
      {p.permalink && <div className="modal-detail-row"><span className="label">Instagram post</span><span className="value"><a href={p.permalink} target="_blank" rel="noreferrer">View on Instagram</a></span></div>}
      {row("Last verified", p.last_checked_at ? fmtStamp(p.last_checked_at, tz) : (p.publish_method === "DIRECT" ? "Not verified yet" : null))}
      {row("Expected caption", p.caption)}
      {row("Actual Instagram caption", p.actual_caption)}
      {row("Publishing status", p.status.label)}
      {p.match_score != null && row("Match confidence", `${p.match_score}% similarity (not a probability)`)}
      {row("Notes", p.notes)}
      {row("Added by", p.created_by_name)}
      {row("Created", fmtStamp(p.created_at, tz))}
      {row("Last updated", fmtStamp(p.updated_at, tz))}
      {err && <div className="cal-error">{err}</div>}
      {p.can_manage && <div className="modal-actions" style={{ flexWrap: "wrap" }}>
        <button className="btn btn-danger" disabled={busy} onClick={del}>Delete</button>
        <button className="btn btn-secondary" disabled={busy} onClick={() => onEdit(p)}>Edit</button>
      </div>}
    </div>
  );
}

export default function Calendar() {
  const { accounts, accountsLoading } = useAppData();
  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();
  const [accountId, setAccountId] = useState(() => { try { return localStorage.getItem("rr_calendarAccount") || ""; } catch { return ""; } });
  const [month, setMonth] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1, 12); });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!accounts.length) return;
    if (!accounts.some((a) => String(a.id) === String(accountId))) setAccountId(String(accounts[0].id));
  }, [accounts, accountId]);
  useEffect(() => { if (accountId) { try { localStorage.setItem("rr_calendarAccount", accountId); } catch { /* ignore */ } } }, [accountId]);

  const cells = useMemo(() => buildCells(month), [month]);
  const account = accounts.find((a) => String(a.id) === String(accountId));

  const load = useCallback(async () => {
    if (!accountId) return;
    setError(null);
    try { setData(await calendarApi.month(accountId, cells[0].key, cells[cells.length - 1].key)); }
    catch (e) { setError(errText(e)); }
    finally { setLoading(false); }
  }, [accountId, cells]);
  useEffect(() => { setLoading(true); setData(null); load(); }, [load]);
  // While plans are waiting to be verified, quietly re-read the calendar so a freshly published post turns green on its own.
  const pending = data?.sync?.pending || 0;
  useEffect(() => {
    if (!pending && !data?.checking) return undefined;
    const t = setInterval(() => { if (document.visibilityState === "visible") load(); }, 30000);
    const first = setTimeout(load, 12000); // the background check started by opening the page usually finishes within seconds
    return () => { clearInterval(t); clearTimeout(first); };
  }, [pending, data?.checking, load]);

  // Crossing the planned time may only become pending; publication requires server evidence.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);
  const live = useCallback((p) => {
    if (p.status.code !== "UPCOMING" || now < Date.parse(p.planned_at)) return p;
    return { ...p, status: { code: "VERIFICATION_PENDING", label: "Checking Instagram", color: "grey", delay_text: null,
      note: "The planned time has passed. Checking Instagram for the planned content." } };
  }, [now]);
  const byDay = useMemo(() => {
    const m = {};
    for (const raw of data?.results || []) { const p = live(raw); (m[p.planned_date] ||= []).push(p); }
    return m;
  }, [data, live]);
  const tz = data?.timezone;
  const todayKey = ymd(new Date());

  const afterSave = (msg) => { closeModal(); showToast(msg); load(); };
  const openForm = (plan, defaultDate) => openModal(
    <PlanForm accountId={accountId} accountName={account?.username} plan={plan} defaultDate={defaultDate} onSaved={afterSave} onCancel={closeModal} />
  );
  const openDetail = (p) => p.auto ? openModal(<AutoDetail p={p} accountName={account?.username} tz={tz} onClose={closeModal} />) : openModal(
    <PlanDetail planId={p.id} accountName={account?.username} tz={tz} onChanged={load} onClose={closeModal}
      onEdit={(plan) => { closeModal(); openForm(plan); }} />
  );
  const openDay = (key) => {
    const items = byDay[key] || [];
    openModal(
      <div className="cal-form">
        <button className="modal-close" onClick={closeModal} aria-label="Close"><svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg></button>
        <h3>{fmtDateLong(key)}</h3>
        {items.length === 0 ? <p className="cal-note">Nothing planned for this day.</p> : items.map((p) => (
          <button type="button" className="cal-day-row" key={p.id} onClick={() => { closeModal(); openDetail(p); }}>
            <span><strong>{fmt12(p.planned_time)}</strong> · {p.kind_label}{entryLabel(p) ? ` · ${entryLabel(p)}` : ""}</span>
            <StatusBadge status={p.status} />
          </button>
        ))}
        <div className="modal-actions"><button className="btn btn-primary btn-block" onClick={() => { closeModal(); openForm(null, key); }}>Add content to this day</button></div>
      </div>
    );
  };

  const checkNow = async () => {
    setChecking(true);
    try {
      const r = await calendarApi.check(accountId);
      showToast(r.checking ? "Instagram verification started. Results will update automatically."
        : r.skipped ? "No unverified entries are waiting to be checked." : "Instagram check completed.");
      await load();
    } catch (e) { showToast(errText(e)); } finally { setChecking(false); }
  };

  if (accountsLoading) return <LoadingState label="Loading accounts..." />;
  if (!accounts.length) {
    return (<><div className="view-header"><div><h2>Content Calendar</h2></div></div>
      <EmptyState title="No Instagram accounts connected" desc="Connect an account under Instagram Accounts to plan its content." /></>);
  }

  return (
    <>
      <div className="view-header">
        <div>
          <h2>Content Calendar</h2>
          <div className="view-subtitle">Plan content by date and track whether it went live on time.</div>
        </div>
      </div>

      <div className="analytics-toolbar metrics-toolbar">
        <div className="metrics-filters">
          <select className="select-filter" aria-label="Instagram account" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            {accounts.map((a) => <option key={a.id} value={a.id}>@{a.username}</option>)}
          </select>
        </div>
        <div className="metrics-actions">
          <span className="metrics-sync-text">{data?.sync?.last_checked_at ? `Last verified ${fmtStamp(data.sync.last_checked_at, tz)}` : "Not verified yet"}</span>
          <button className="btn btn-secondary btn-sm" onClick={checkNow} disabled={checking || data?.checking}>{checking || data?.checking ? "Checking…" : "Check Instagram"}</button>
          <button className="btn btn-primary btn-sm" onClick={() => openForm(null, todayKey)}>Add Content</button>
        </div>
      </div>

      <div className="calendar-controls">
        <div className="calendar-nav">
          <button className="calendar-nav-btn" aria-label="Previous month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1, 12))}><svg viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6" /></svg></button>
          <div className="calendar-month-label">{MONTHS[month.getMonth()]} {month.getFullYear()}</div>
          <button className="calendar-nav-btn" aria-label="Next month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1, 12))}><svg viewBox="0 0 24 24"><path d="M9 18l6-6-6-6" /></svg></button>
        </div>
        <div className="cal-legend">{LEGEND.map(([c, t]) => <span key={t}><i className={`cal-dot ${c}`} />{t}</span>)}</div>
      </div>

      {loading || (!data && !error) ? <LoadingState label="Loading calendar..." /> : error ? <ErrorState message={error} onRetry={load} /> : (
        <>
          {data.sync?.error && <div className="metrics-note" role="status">{data.sync.error === "Calendar verification failed. Please retry or contact an administrator." ? "Instagram verification is temporarily unavailable. Please try again later." : data.sync.error}</div>}
          {data.results.length === 0 && <div className="metrics-note">Nothing is planned for @{account?.username} in this view yet. Click a date or "Add Content" to plan something.</div>}
          <div className="calendar-grid cal-planner">
            {WEEKDAYS.map((d) => <div key={d} className="calendar-weekday">{d}</div>)}
            {cells.map((c) => {
              const items = byDay[c.key] || [];
              return (
                <div key={c.key} className={`calendar-cell ${c.outside ? "outside" : ""} ${c.key === todayKey ? "today" : ""}`}>
                  <button type="button" className="cal-date-btn" onClick={() => (items.length ? openDay(c.key) : openForm(null, c.key))} aria-label={`${fmtDateLong(c.key)}: ${items.length} planned`}>
                    <span className="calendar-date-num">{c.day}</span>
                  </button>
                  {items.slice(0, MAX_PER_CELL).map((p) => (
                    <button type="button" key={p.id} className={`calendar-item cal-chip ${p.status.color}`} onClick={() => openDetail(p)} title={`${statusLabel(p.status)}${p.status.delay_text ? ` — ${p.status.delay_text}` : ""}`}>
                      <strong>{fmt12(p.planned_time)} · {p.kind_label}</strong>
                      <span>{entryLabel(p) || statusLabel(p.status)}</span>
                      <em>{p.status.delay_text || statusLabel(p.status)}</em>
                    </button>
                  ))}
                  {items.length > MAX_PER_CELL && <button type="button" className="cal-more" onClick={() => openDay(c.key)}>+{items.length - MAX_PER_CELL} more</button>}
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}
